/**
 * News page - Full news feed across all portfolio symbols
 *
 * Features:
 * - Filter by symbol, sentiment, material only
 * - Infinite scroll or pagination
 * - Aggregated sentiment statistics
 */
import { useState, useMemo, useCallback } from 'react'
import { useHoldings, useWatchlist } from '../hooks'
import { useNewsFeed, useSymbolsWithNews } from '../hooks/useNews'
import type { NewsArticleListItem, SentimentType } from '../types/news'

// =============================================================================
// Types
// =============================================================================

type TimeFilter = 7 | 14 | 30 | 90

interface FilterState {
  symbols: string[]
  sentiment: SentimentType | 'all'
  materialOnly: boolean
  days: TimeFilter
}

// =============================================================================
// Constants
// =============================================================================

const TIME_FILTER_OPTIONS: { value: TimeFilter; label: string }[] = [
  { value: 7, label: '7 Days' },
  { value: 14, label: '14 Days' },
  { value: 30, label: '30 Days' },
  { value: 90, label: '90 Days' },
]

const SENTIMENT_OPTIONS: { value: SentimentType | 'all'; label: string }[] = [
  { value: 'all', label: 'All Sentiment' },
  { value: 'positive', label: 'Positive' },
  { value: 'negative', label: 'Negative' },
  { value: 'neutral', label: 'Neutral' },
]

const PAGE_SIZE = 50

// =============================================================================
// Helper Components
// =============================================================================

function SentimentBadge({ sentiment }: { sentiment: SentimentType | null }) {
  if (!sentiment) {
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600">
        Unanalyzed
      </span>
    )
  }

  const styles: Record<SentimentType, string> = {
    positive: 'bg-green-100 text-green-800',
    negative: 'bg-red-100 text-red-800',
    neutral: 'bg-gray-100 text-gray-600',
  }

  const labels: Record<SentimentType, string> = {
    positive: 'Positive',
    negative: 'Negative',
    neutral: 'Neutral',
  }

  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${styles[sentiment]}`}
    >
      {labels[sentiment]}
    </span>
  )
}

function MaterialBadge() {
  return (
    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-800">
      Material
    </span>
  )
}

function SymbolBadge({ symbol }: { symbol: string }) {
  return (
    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold bg-blue-100 text-blue-800">
      {symbol}
    </span>
  )
}

function formatTimeAgo(dateStr: string): string {
  const date = new Date(dateStr)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60))
  const diffDays = Math.floor(diffHours / 24)

  if (diffHours < 1) {
    return 'Just now'
  }
  if (diffHours < 24) {
    return `${diffHours}h ago`
  }
  if (diffDays < 7) {
    return `${diffDays}d ago`
  }
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  })
}

function SummaryStat({
  label,
  value,
  variant = 'default',
}: {
  label: string
  value: string | number
  variant?: 'default' | 'positive' | 'negative' | 'neutral'
}) {
  const variantStyles = {
    default: 'text-gray-900',
    positive: 'text-green-600',
    negative: 'text-red-600',
    neutral: 'text-gray-600',
  }

  return (
    <div className="bg-white rounded-lg shadow p-4 text-center">
      <p className="text-xs text-gray-500 font-medium uppercase tracking-wider">{label}</p>
      <p className={`mt-1 text-2xl font-semibold ${variantStyles[variant]}`}>{value}</p>
    </div>
  )
}

function ArticleSkeleton() {
  return (
    <div className="p-4 rounded-lg border border-gray-200 bg-white animate-pulse">
      <div className="flex gap-4">
        <div className="flex-shrink-0 w-24 h-24 bg-gray-200 rounded" />
        <div className="flex-1 space-y-2">
          <div className="h-5 w-3/4 bg-gray-200 rounded" />
          <div className="h-4 w-1/2 bg-gray-200 rounded" />
          <div className="flex gap-2 mt-2">
            <div className="h-5 w-16 bg-gray-200 rounded" />
            <div className="h-5 w-20 bg-gray-200 rounded" />
          </div>
        </div>
      </div>
    </div>
  )
}

// =============================================================================
// Article Card Component
// =============================================================================

function ArticleCard({ article }: { article: NewsArticleListItem }) {
  const getSentimentBorderColor = (sentiment: SentimentType | null): string => {
    if (!sentiment) return 'border-gray-200'
    const colors: Record<SentimentType, string> = {
      positive: 'border-l-green-500',
      negative: 'border-l-red-500',
      neutral: 'border-l-gray-400',
    }
    return colors[sentiment]
  }

  return (
    <a
      href={article.url}
      target="_blank"
      rel="noopener noreferrer"
      className={`block p-4 rounded-lg border bg-white hover:bg-gray-50 transition-colors border-l-4 ${
        article.is_material ? 'border-l-amber-500' : getSentimentBorderColor(article.sentiment)
      }`}
    >
      <div className="flex gap-4">
        {/* Thumbnail */}
        {article.image_url && (
          <div className="flex-shrink-0 w-24 h-24 rounded overflow-hidden bg-gray-100">
            <img
              src={article.image_url}
              alt=""
              className="w-full h-full object-cover"
              onError={(e) => {
                (e.target as HTMLImageElement).style.display = 'none'
              }}
            />
          </div>
        )}

        {/* Content */}
        <div className="flex-1 min-w-0">
          {/* Header row with symbol */}
          <div className="flex items-center gap-2 mb-1">
            {article.symbol && <SymbolBadge symbol={article.symbol} />}
            <span className="text-sm text-gray-500">{article.source}</span>
            <span className="text-gray-300">-</span>
            <span className="text-sm text-gray-500">{formatTimeAgo(article.published_at)}</span>
          </div>

          {/* Headline */}
          <h3 className="font-medium text-gray-900 line-clamp-2 hover:text-primary-600">
            {article.headline}
          </h3>

          {/* Badges row */}
          <div className="flex flex-wrap items-center gap-2 mt-2">
            <SentimentBadge sentiment={article.sentiment} />
            {article.is_material && <MaterialBadge />}
            {article.sentiment_score !== null && (
              <span className="text-xs text-gray-500">
                Score: {article.sentiment_score.toFixed(2)}
              </span>
            )}
          </div>

          {/* AI Summary */}
          {article.ai_summary && (
            <p className="mt-2 text-sm text-gray-600 line-clamp-2">
              {article.ai_summary}
            </p>
          )}
        </div>
      </div>
    </a>
  )
}

// =============================================================================
// Filter Components
// =============================================================================

function FilterBar({
  filters,
  availableSymbols,
  onFilterChange,
}: {
  filters: FilterState
  availableSymbols: string[]
  onFilterChange: (filters: Partial<FilterState>) => void
}) {
  return (
    <div className="bg-white rounded-lg shadow p-4">
      <div className="flex flex-wrap items-center gap-4">
        {/* Symbol multi-select */}
        <div className="flex-1 min-w-[200px]">
          <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">
            Symbols
          </label>
          <select
            multiple
            value={filters.symbols}
            onChange={(e) => {
              const selected = Array.from(e.target.selectedOptions, (opt) => opt.value)
              onFilterChange({ symbols: selected })
            }}
            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
            size={3}
          >
            {availableSymbols.map((symbol) => (
              <option key={symbol} value={symbol}>
                {symbol}
              </option>
            ))}
          </select>
          {filters.symbols.length > 0 && (
            <button
              type="button"
              onClick={() => onFilterChange({ symbols: [] })}
              className="mt-1 text-xs text-primary-600 hover:text-primary-700"
            >
              Clear selection
            </button>
          )}
        </div>

        {/* Sentiment filter */}
        <div>
          <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">
            Sentiment
          </label>
          <select
            value={filters.sentiment}
            onChange={(e) => onFilterChange({ sentiment: e.target.value as SentimentType | 'all' })}
            className="px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
          >
            {SENTIMENT_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        {/* Time filter */}
        <div>
          <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">
            Time Period
          </label>
          <select
            value={filters.days}
            onChange={(e) => onFilterChange({ days: Number(e.target.value) as TimeFilter })}
            className="px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
          >
            {TIME_FILTER_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        {/* Material only toggle */}
        <div className="flex items-center">
          <label className="flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={filters.materialOnly}
              onChange={(e) => onFilterChange({ materialOnly: e.target.checked })}
              className="h-4 w-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
            />
            <span className="ml-2 text-sm text-gray-700">Material only</span>
          </label>
        </div>
      </div>
    </div>
  )
}

// =============================================================================
// Main Component
// =============================================================================

export function NewsPage() {
  // Filter state
  const [filters, setFilters] = useState<FilterState>({
    symbols: [],
    sentiment: 'all',
    materialOnly: false,
    days: 7,
  })

  // Pagination state
  const [page, setPage] = useState(0)

  // Get holdings and watchlist for symbol dropdown
  const { data: holdings } = useHoldings()
  const { data: watchlist } = useWatchlist()
  const { data: newsSymbols } = useSymbolsWithNews()

  // Build available symbols from holdings + watchlist + symbols with news
  const availableSymbols = useMemo(() => {
    const symbolSet = new Set<string>()

    // Add holding symbols
    holdings?.forEach((h) => symbolSet.add(h.symbol))

    // Add watchlist symbols
    watchlist?.forEach((w) => symbolSet.add(w.symbol))

    // Add symbols that have news
    newsSymbols?.symbols.forEach((s) => symbolSet.add(s))

    return Array.from(symbolSet).sort()
  }, [holdings, watchlist, newsSymbols])

  // Handle filter changes
  const handleFilterChange = useCallback((partial: Partial<FilterState>) => {
    setFilters((prev) => ({ ...prev, ...partial }))
    setPage(0) // Reset to first page on filter change
  }, [])

  // Build query params
  const queryParams = useMemo(() => {
    return {
      symbols: filters.symbols.length > 0 ? filters.symbols : undefined,
      days: filters.days,
      limit: PAGE_SIZE,
      offset: page * PAGE_SIZE,
      sentiment: filters.sentiment !== 'all' ? filters.sentiment : undefined,
      materialOnly: filters.materialOnly,
    }
  }, [filters, page])

  // Fetch news feed
  const {
    data: feedData,
    isLoading,
    error,
  } = useNewsFeed(queryParams)

  const articles = feedData?.articles ?? []
  const totalPages = Math.ceil((feedData?.total ?? 0) / PAGE_SIZE)

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">News Feed</h1>
      </div>

      {/* Filters */}
      <FilterBar
        filters={filters}
        availableSymbols={availableSymbols}
        onFilterChange={handleFilterChange}
      />

      {/* Summary stats */}
      {feedData && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <SummaryStat label="Total" value={feedData.total} />
          <SummaryStat label="Positive" value={feedData.positive_count} variant="positive" />
          <SummaryStat label="Negative" value={feedData.negative_count} variant="negative" />
          <SummaryStat label="Material" value={feedData.material_count} />
        </div>
      )}

      {/* Error state */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
          Failed to load news feed. Please try again.
        </div>
      )}

      {/* Loading state */}
      {isLoading && (
        <div className="space-y-4">
          {Array.from({ length: 5 }).map((_, i) => (
            <ArticleSkeleton key={i} />
          ))}
        </div>
      )}

      {/* Articles list */}
      {!isLoading && !error && (
        <>
          {articles.length > 0 ? (
            <div className="space-y-4">
              {articles.map((article) => (
                <ArticleCard key={article.id} article={article} />
              ))}
            </div>
          ) : (
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-8 text-center">
              <svg
                className="mx-auto h-12 w-12 text-gray-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z"
                />
              </svg>
              <h3 className="mt-4 text-sm font-medium text-gray-900">No news articles</h3>
              <p className="mt-2 text-sm text-gray-500">
                No news articles found matching your filters.
                {filters.symbols.length > 0 && ' Try selecting different symbols.'}
                {filters.materialOnly && ' Try disabling the "Material only" filter.'}
              </p>
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between border-t border-gray-200 pt-4">
              <p className="text-sm text-gray-500">
                Showing {page * PAGE_SIZE + 1} - {Math.min((page + 1) * PAGE_SIZE, feedData?.total ?? 0)} of{' '}
                {feedData?.total ?? 0} articles
              </p>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setPage((p) => Math.max(0, p - 1))}
                  disabled={page === 0}
                  className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                    page === 0
                      ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                      : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  Previous
                </button>
                <button
                  type="button"
                  onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                  disabled={page >= totalPages - 1}
                  className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                    page >= totalPages - 1
                      ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                      : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  )
}

export default NewsPage
